import dayjs from 'dayjs'

// src/shared/utils/utilities.jsx and src/alive-academics/utils/utilities.js.
export const getNameInitials = ( name ) => {
    try {
        const nameParts = name?.split( " " )
        return nameParts.map( n => n?.at( 0 ) ).slice( 0, 2 )
    } catch ( err ) {
        return name?.at( 0 )
    }
}

export const isReactComponent = ( value ) => {
    return (
        typeof value === 'function' ||
        ( value && typeof value === 'object' && value.$$typeof === Symbol.for( 'react.element' ) )
    )
}

export const getGroupChipLabel = groupDetails => {
    if ( groupDetails.entity === 'batch' || groupDetails.batch_assignment_id ) {
        return groupDetails.chip_label || `Batch: ${groupDetails.batch_name}${groupDetails.remarks ? `, ${groupDetails.remarks}` : "Remarks: Not given."}`
    }
    if ( groupDetails.entity === 'section' || groupDetails.section_assignment_id ) {
        return groupDetails.chip_label || `Section: ${groupDetails.section_short_name} `
    }
    return ""
}

export const getTransformedGroupDetails = group => {
    if ( group.entity === 'batch' || group.batch_assignment_id ) {
        const batch = { ...group }
        if ( !batch.course_name_short )
            batch.course_name_short = batch.course_short_name
        batch.id = batch.batch_assignment_id
        batch.entity = 'batch'
        batch.title = `${batch.batch_name}-${batch.course_name_short || ""}${batch.current_year ? ` Y${batch.current_year}` : ``}${batch.current_sem ? ` S${batch.current_sem}` : ``} ${batch.batch_short_name}`
        batch.short_title = `Batch ${batch.batch_name || batch.batch_short_name}${batch.remarks ? `, ${batch.remarks}` : ", Remarks: Not given."}`
        batch.chip_label = `Batch: ${batch.batch_name || batch.batch_short_name}${batch.remarks ? `, ${batch.remarks}` : ", Remarks: Not given."}`
        batch.academic_details = `${batch.ac_year ? `${batch.ac_year},` : ""} ${batch.current_year ? `Year: ${batch.current_year}` : ""}${batch.current_sem ? `, Semester: ${batch.current_sem}` : ""}, Remarks: ${batch.remarks || "Not given."}`
        batch.course_name_short = Array.from( new Set( batch.course_name_short?.split( "," ) ) ).join( ", " )
        return batch
    }
    if ( group.entity === 'section' || group.section_assignment_id ) {
        const section = { ...group }
        section.id = `${section.section_assignment_id}-${section.section_short_name}`
        section.entity = 'section'
        section.title = `${section.course_branch_short_name}-${section.course_short_name || ""}${section.current_year ? ` Y${section.current_year}` : ``}${section.current_sem ? ` S${section.current_sem}` : ``} (${section.section_short_name + ")"}`
        section.short_title = `Section ${section.section_short_name}`
        section.chip_label = `Section: ${section.section_short_name} `
        section.academic_details = `${section.ac_year ? `${section.ac_year},` : ""} ${section.current_year ? `Year: ${section.current_year}` : ""}${section.current_sem ? `, Semester: ${section.current_sem}` : ""}, Remarks: ${section.remarks || "Not given."}`
        section.course_name_short = section.course_short_name
        return section
    }
}

// getCleanSubjectDetails only existed in alive-academics/utils/utilities.js
export const getCleanSubjectDetails = subject => {
    if ( !subject )
        return null
    return {
        subject_id: subject.subject_id,
        subject_code: subject.subject_code,
        subject_name_short: subject.subject_name_short,
        subject_name: subject.subject_name,
        ac_year_id: subject.ac_year_id,
        ac_year: subject.ac_year,
        year: subject.year,
        sem: subject.sem,
    }
}

// markdownToText only existed in alive-academics/utils/utilities.js
export const markdownToText = ( markdown ) => {
    if ( !markdown ) return ''
    return markdown
        .replace( /(\*\*|__)(.*?)\1/g, "$2" ) // Bold
        .replace( /(\*|_)(.*?)\1/g, "$2" )   // Italics
        .replace( /~~(.*?)~~/g, "$1" )       // Strikethrough
        .replace( /`{1,2}([^`]*)`{1,2}/g, "$1" ) // Inline code
        .replace( /!\[.*?\]\((.*?)\)/g, "" ) // Remove images
        .replace( /\[([^\]]+)\]\((.*?)\)/g, "$1" ) // Links: keep text, remove URL
        .replace( /^#{1,6}\s*(.*)/gm, "$1" ) // Headers
        .replace( />\s?(.*)/g, "$1" )       // Blockquotes
        .replace( /[-*+]\s+(.*)/g, "$1" )   // Lists
        .replace( /\n{2,}/g, "\n" )         // Remove extra newlines
        .trim();
}

export const getKeyValueFromObject = ( obj, keys ) => {
    if ( typeof keys === 'string' ) {
        return obj ? obj[keys] : undefined
    }

    if ( Array.isArray( keys ) ) {
        return keys.reduce( ( acc, key ) => ( acc ? acc[key] : undefined ), obj )
    }

    return undefined
}

export const boundaryValue = ( min, desired, max ) => {
    if ( desired == null ) {
        return min
    }
    return Math.max( min, Math.min( desired, max ) )
}

export const convertToCDNLink = ( data = "" ) => {
    if ( typeof data !== 'string' || data.includes( "cdn.digitaloceanspaces" ) )
        return data
    return data?.replace( /digitaloceanspaces/, 'cdn.digitaloceanspaces' );
}

export const replaceSpacesesLocationsWithCDNLinks = ( data ) => {
    if ( typeof data === 'string' ) {
        const regex = /https:\/\/[a-z-]+\.[a-zA-Z0-9]+\.digitaloceanspaces\.com\/(?<key>(?!cdn\/)[a-zA-Z_/-]+\/[.a-zA-Z0-9@%()_-\s]+\.\w+\/?)/gm
        const result = data.replace( regex, ( match ) => {
            return match.replace( '.digitaloceanspaces.com', '.cdn.digitaloceanspaces.com' )
        } )
        return result
    } else return data
}

export const extractSpacesLinkFromText = ( data ) => {
    if ( typeof data === 'string' ) {
        const regex = /https:\/\/(?<bucket>[a-z-]+)\.(?<region>[a-zA-Z0-9]+)\.(cdn\.)?digitaloceanspaces\.com\/(?<key>[a-zA-Z_/-]+\/[.a-zA-Z0-9@%()_-\s]+\.\w+\/?)/gm
        const result = Array.from( data.matchAll( regex ) )
        const matched = result.map( ( r ) => r[0] )
        return { matched, matchedWithGroups: result }
    } else return data
}

export const getUniqueStringFromLongString = ( input ) => {
    input = input + ''
    let hash = 0
    for ( let i = 0; i < input.length; i++ ) {
        hash = ( hash << 5 ) - hash + input.charCodeAt( i )
        hash &= hash // Convert to 32-bit integer
    }
    return Math.abs( hash ).toString( 36 ) // Convert to base-36 for compactness
}

export const getFileSizeFromBytes = ( bytes ) => {
    if ( isNaN( bytes ) ) return '-'
    const kb = bytes / 1024
    if ( kb < 1024 ) return `${kb?.toFixed( 2 )} KB`
    else if ( kb / 1024 < 1024 ) return `${( kb / 1024 )?.toFixed( 2 )} MB`
    else return `${( kb / ( 1024 * 1024 ) )?.toFixed( 2 )} GB`
}

export const getCountString = ( count ) => {
    if ( Number.isNaN( +count ) ) return '_'
    if ( count < 1000 ) return count
    const inKilo = count / 1000
    if ( +inKilo < 1000 ) {
        const [whole, decimal = '0'] = ( '' + inKilo ).split( '.' )
        return `${whole}.${decimal[0]}K+`
    }
    const inMil = ( +inKilo / 1000 ).toFixed( 1 )
    const [whole, decimal = '0'] = ( '' + inMil ).split( '.' )
    return `${whole}.${decimal[0]}M+`
}

const createImage = ( url ) =>
    new Promise( ( resolve, reject ) => {
        const image = new Image()
        image.addEventListener( 'load', () => resolve( image ) )
        image.addEventListener( 'error', ( error ) => reject( error ) )
        image.setAttribute( 'crossOrigin', 'anonymous' ) // needed to avoid cross-origin issues on CodeSandbox
        image.src = url
    } )

export const getCroppedImage = async ( imageSrc, pixelCrop ) => {
    const image = await createImage( imageSrc )
    const canvas = document.createElement( 'canvas' )
    const ctx = canvas.getContext( '2d' )

    if ( !ctx ) {
        return null
    }

    const { width: bBoxWidth, height: bBoxHeight } = image

    canvas.width = bBoxWidth
    canvas.height = bBoxHeight

    ctx.translate( bBoxWidth / 2, bBoxHeight / 2 )
    ctx.translate( -image.width / 2, -image.height / 2 )

    ctx.drawImage( image, 0, 0 )

    const data = ctx.getImageData( pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height )

    canvas.width = pixelCrop.width
    canvas.height = pixelCrop.height

    ctx.putImageData( data, 0, 0 )

    return await new Promise( ( resolve, reject ) => {
        canvas.toBlob( ( file ) => {
            file.name = 'cropped.jpeg'
            resolve( { file: file, url: URL.createObjectURL( file ) } )
        }, 'image/jpeg' )
    } )
}

export const base64ToFileAndBlob = async ( base64String, fileName, mimeType ) => {
    const byteCharacters = atob( base64String.split( ',' )[1] )

    const byteArray = new Uint8Array( byteCharacters.length )

    for ( let i = 0; i < byteCharacters.length; i++ ) {
        byteArray[i] = byteCharacters.charCodeAt( i )
    }

    const blob = new Blob( [byteArray], { type: mimeType } )

    const file = new File( [blob], fileName, { type: mimeType } )

    return { file, blob }
}

export const extractMimeType = ( base64String ) => {
    const regex = /^data:(image|video|audio|application|text|font)\/([a-zA-Z0-9-+.]+);base64,/
    const match = base64String.match( regex )

    if ( match ) {
        return match[0]?.split( ':' )[1]?.split( ';' )[0]
    } else {
        console.error( 'Invalid base64 data URL' )
        return null
    }
}

export const shuffleArray = ( array ) => {
    let currentIndex = array.length,
        randomIndex
    while ( currentIndex !== 0 ) {
        randomIndex = Math.floor( Math.random() * currentIndex )
        currentIndex--
            ;[array[currentIndex], array[randomIndex]] = [array[randomIndex], array[currentIndex]]
    }

    return array
}

export const mergeArray = ( indexes, arrayList ) => {
    let newArray = []
    for ( let i = 0; i < indexes.length; i++ ) {
        const index = indexes[i]
        newArray = newArray.concat( arrayList[index] )
    }
    return newArray
}

let debounceTimeoutIds = {}
export const debounce = ( debounceId, func, delay = 500 ) => {
    return ( ...args ) => {
        if ( debounceTimeoutIds[debounceId] ) clearTimeout( debounceTimeoutIds[debounceId] )
        debounceTimeoutIds[debounceId] = setTimeout( () => {
            func.apply( null, args )
        }, delay )
    }
}

export const getMaxValueByKey = ( list, key ) => {
    if ( !Array.isArray( list ) ) throw new Error( `First argument must be an array, reaceived ${typeof list}` )
    return list.reduce( ( max, item ) => ( item[key] > max ? item[key] : max ), 0 )
}

export const sortArrayOfObjectsByKey = ( list, key, order = 1, dataType = "string" ) => {
    if ( dataType === "string" ) {
        const sortedList = [...list]
        sortedList.sort( ( a, b ) => a[key].localeCompare( b[key] ) )
        if ( order === -1 ) sortedList.reverse()
        return sortedList
    } else {
        const sortedList = [...list]
        sortedList.sort( ( a, b ) => a[key] > b[key] ? 1 : -1 )
        if ( order === -1 ) sortedList.reverse()
        return sortedList
    }
}

export const sortByProperty = ( data, setData, property, type, order ) => {
    let newFilteredData = [...data]

    const getNestedProperty = ( obj, keys ) => {
        return keys.reduce( ( acc, key ) => acc && acc[key], obj )
    }

    const keys = property?.split( '.' )
    newFilteredData.sort( ( a, b ) => {
        const aval = getNestedProperty( a, keys )
        const bval = getNestedProperty( b, keys )

        if ( type === 'number' ) {
            if ( order === '1' ) {
                return parseInt( aval ) - parseInt( bval )
            } else {
                return parseInt( bval ) - parseInt( aval )
            }
        } else {
            if ( order === '1' ) {
                return aval?.localeCompare( bval, undefined, { sensitivity: 'base' } )
            } else {
                return bval?.localeCompare( aval, undefined, { sensitivity: 'base' } )
            }
        }
    } )
    setData( newFilteredData )
}

/**
 * Sorts an array of objects by a specific key using a custom weight mapping.
 * (sortByEnumWeight only existed in shared/utils/utilities.jsx, not in alive-academics/utilities.js)
 *
 * @param {Array<Object>} arr - The array of objects to sort.
 * @param {string} key - The object property containing the enum values.
 * @param {Object<string, number>} weightMap - Mapping of enum values to numeric weights.
 * @param {number} [defaultWeight=Infinity] - Weight for values not found in the map.
 * @param {boolean} [ascending=true] - True for ascending order, false for descending.
 * @returns {Array<Object>} A new sorted array.
 */
export const sortByEnumWeight = ( arr, key, weightMap, ascending = true, defaultWeight = Infinity ) => {
    return [...arr].sort( ( a, b ) => {
        const weightA = weightMap[a[key]] !== undefined ? weightMap[a[key]] : defaultWeight
        const weightB = weightMap[b[key]] !== undefined ? weightMap[b[key]] : defaultWeight

        return ascending ? weightA - weightB : weightB - weightA
    } )
}



// STAGE ENV DUMMY DATA
export const dummyRoom = [
    {
        "subject_id": 873,
        "course_branch_assignment_id": "57,117,118,119",
        "subject_name_short": "DBMS",
        "course_assignment_id": "20,20,20,20",
        "time_table_id": 925998,
        "batch_assignment_id": "",
        "subject_assignment_id": 6771,
        "institute_id": 1,
        "current_sem": 1,
        "duration": 55,
        "batch_short_name": "NA",
        "current_year": 1,
        "branch_id": 12,
        "branch_name": "Master of Computer Applications",
        "selected_date": dayjs().format( "YYYY-MM-DD" ),
        // "sTime":  "08:10:00.0000000",
        "sTime": dayjs().subtract( 5, 'minutes' ).format( "HH:mm:ss.SSS" ),
        "course_branch_short_name": "MCA,MCAI,MCADS,MCA CC",
        "empID": "AI00441",
        "program_assignment_id": null,
        // "eTime": "11:20:00.0000000",
        "eTime": dayjs().add( 1, 'hour' ).format( "HH:mm:ss.SSS" ),
        // "end_time": "11:20 AM",
        "end_time": dayjs().add( 1, 'hour' ).format( "h:mm A" ),
        "presenter_name": "Sumit Singha Chowdhury",
        "institute_name": "ACHARYA INSTITUTE OF TECHNOLOGY",
        "institute_name_short": "AIT",
        // "start_time": "8:20 AM",
        "start_time": dayjs().subtract( 5, 'minutes' ).format( "h:mm A" ),
        "course_short_name": "MCA,MCA,MCA,MCA",
        "intervals": `${dayjs().subtract( 5, 'minutes' ).format( "h:mm A" )} - ${dayjs().add( 1, 'hour' ).format( "h:mm A" )}`,
        "section_short_name": "B",
        "online": true,
        "is_hybrid_course": true,
        "interval_type_id": 1,
        "section_assignment_id": 1005
    }
]